// pages/Agents/AgentSpace/AgentSpaceSidebar.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    Drawer,
    Tooltip,
    Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import EmailIcon from '@mui/icons-material/Email';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import axiosInstance from '../../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
};

// Map icon_name from backend to React component
const ICON_MAP = {
    PollIcon: (size = 18) => <PollIcon sx={{ fontSize: size }} />,
    PeopleIcon: (size = 18) => <PeopleIcon sx={{ fontSize: size }} />,
    ChatIcon: (size = 18) => <ChatIcon sx={{ fontSize: size }} />,
    EventIcon: (size = 18) => <EventIcon sx={{ fontSize: size }} />,
    MailOutlineIcon: (size = 18) => <MailOutlineIcon sx={{ fontSize: size }} />,
    EmailIcon: (size = 18) => <EmailIcon sx={{ fontSize: size }} />,
    CalendarMonthIcon: (size = 18) => <CalendarMonthIcon sx={{ fontSize: size }} />,
    CleaningServicesIcon: (size = 18) => <CleaningServicesIcon sx={{ fontSize: size }} />,
};

function getIcon(iconName, size = 18) {
    const iconFn = ICON_MAP[iconName];
    return iconFn ? iconFn(size) : <AutoAwesomeIcon sx={{ fontSize: size }} />;
}

// Fallback agent types when API unavailable
const FALLBACK_AGENT_TYPES = [
    { id: 'survey', label: 'Surveys', icon_name: 'PollIcon', color: '#3b82f6' },
    { id: 'crm', label: 'CRM', icon_name: 'PeopleIcon', color: '#f59e0b' },
    { id: 'chat_history', label: 'Chat History', icon_name: 'ChatIcon', color: '#6366f1' },
    { id: 'events', label: 'Events', icon_name: 'EventIcon', color: '#059669' },
    { id: 'gmail', label: 'Gmail', icon_name: 'MailOutlineIcon', color: '#dc2626' },
    { id: 'outlook', label: 'Outlook', icon_name: 'EmailIcon', color: '#0078d4' },
    { id: 'gmail-calendar', label: 'Calendar', icon_name: 'CalendarMonthIcon', color: '#0891b2' },
    { id: 'gmail_cleanup', label: 'Gmail Cleanup', icon_name: 'CleaningServicesIcon', color: '#7c3aed' },
];

export default function AgentSpaceSidebar({
    isOpen,
    isCollapsed = false,
    onToggle,
    onConversationSelect,
    currentSessionCode,
    chatbotId,
    isMobile = false,
    activeView = 'chat',
    agentTypeFilter = null,
    onResourceClick,
    onInitialAgentType,
    onNewTask,
}) {
    const [conversations, setConversations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState('');

    // Dynamic agent types from backend
    const [agentTypes, setAgentTypes] = useState([]);
    const [loadingAgentTypes, setLoadingAgentTypes] = useState(false);

    // Fetch agent types from backend (business-account filtered)
    useEffect(() => {
        if (!chatbotId) return;
        let cancelled = false;
        setLoadingAgentTypes(true);
        axiosInstance.get('/api/agent-space/agent-types')
            .then(res => {
                if (!cancelled) {
                    setAgentTypes(res.data.success && res.data.data?.length ? res.data.data : FALLBACK_AGENT_TYPES);
                }
            })
            .catch(() => { if (!cancelled) setAgentTypes(FALLBACK_AGENT_TYPES); })
            .finally(() => { if (!cancelled) setLoadingAgentTypes(false); });
        return () => { cancelled = true; };
    }, [chatbotId]);

    // Derive task counts and recency per agent type from conversations
    const agentStats = useMemo(() => {
        const stats = {};
        for (const conv of conversations) {
            const at = conv.agent_type;
            if (!at) continue;
            if (!stats[at]) {
                stats[at] = { count: 0, latestTimestamp: null };
            }
            stats[at].count += 1;
            const ts = conv.updated_at || conv.created_at;
            if (ts && (!stats[at].latestTimestamp || ts > stats[at].latestTimestamp)) {
                stats[at].latestTimestamp = ts;
            }
        }
        return stats;
    }, [conversations]);

    // Sort agent types: recently used first (by latest conversation timestamp), then alphabetically
    const sortedAgentTypes = useMemo(() => {
        return [...agentTypes].sort((a, b) => {
            const aTs = agentStats[a.id]?.latestTimestamp || '';
            const bTs = agentStats[b.id]?.latestTimestamp || '';
            // Agents with conversations first, sorted by most recent
            if (aTs && !bTs) return -1;
            if (!aTs && bTs) return 1;
            if (aTs && bTs) return bTs.localeCompare(aTs);
            return 0;
        });
    }, [agentTypes, agentStats]);

    // Auto-select first agent type when dashboard activates
    useEffect(() => {
        if (activeView === 'dashboard' && sortedAgentTypes.length > 0 && !agentTypeFilter) {
            const firstType = sortedAgentTypes[0].id;
            if (onInitialAgentType) onInitialAgentType(firstType);
            if (onResourceClick) onResourceClick(firstType);
        }
    }, [activeView, sortedAgentTypes, agentTypeFilter]);

    useEffect(() => {
        if (isOpen && chatbotId) loadConversations();
    }, [isOpen, chatbotId]);

    useEffect(() => {
        if (isOpen && currentSessionCode && chatbotId) loadConversations();
    }, [currentSessionCode]);

    const loadConversations = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/agent-space/conversations?chatbot_id=${chatbotId}`);
            if (response.data.success) {
                setConversations(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load agent space conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNewChat = () => {
        if (onNewTask) {
            onNewTask();
        } else {
            onConversationSelect(null);
        }
        if (isMobile) onToggle();
    };

    const handleSelect = (sessionCode) => {
        onConversationSelect(sessionCode);
        if (isMobile) onToggle();
    };

    const handleDelete = async (sessionCode, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this conversation?')) return;
        try {
            const resp = await axiosInstance.delete(`/api/agent-space/conversations/${sessionCode}`);
            if (resp.data.success) {
                setConversations((prev) => prev.filter((c) => c.session_code !== sessionCode));
                if (sessionCode === currentSessionCode) handleNewChat();
            }
        } catch (error) {
            console.error('Failed to delete conversation:', error);
        }
    };

    const startEditing = (conv, e) => {
        e.stopPropagation();
        setEditingId(conv.session_code);
        setEditTitle(conv.title);
    };

    const handleRename = async (sessionCode) => {
        if (!editTitle.trim()) return;
        try {
            const resp = await axiosInstance.patch(`/api/agent-space/conversations/${sessionCode}/title`, {
                title: editTitle.trim(),
            });
            if (resp.data.success) {
                setConversations((prev) =>
                    prev.map((c) =>
                        c.session_code === sessionCode ? { ...c, title: editTitle.trim() } : c
                    )
                );
                setEditingId(null);
            }
        } catch (error) {
            console.error('Failed to rename:', error);
        }
    };

    const filtered = conversations.filter((c) =>
        (c.title || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const mins = Math.floor(diff / 60000);
        const hrs = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        if (mins < 60) return `${mins}m ago`;
        if (hrs < 24) return `${hrs}h ago`;
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
    };

    const sidebarContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'space-between',
                px: 2, py: 1.5,
                borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}>
                {!isCollapsed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AutoAwesomeIcon sx={{ fontSize: 18, color: colors.accent }} />
                        <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 600, color: colors.textPrimary }}>
                            {activeView === 'dashboard' ? 'Agents' : 'Tasks'}
                        </Typography>
                    </Box>
                )}
                <IconButton
                    size="small"
                    onClick={onToggle}
                    sx={{ color: colors.textTertiary, '&:hover': { bgcolor: 'rgba(139,92,246,0.1)', color: colors.accent } }}
                >
                    <MenuOpenIcon sx={{ fontSize: 18, transform: isCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
                </IconButton>
            </Box>

            {/* New Task + Search (hidden in dashboard view) */}
            {activeView !== 'dashboard' && (
                <>
                    <Box sx={{ p: 1.5, px: isCollapsed ? 1 : 1.5 }}>
                        {isCollapsed ? (
                            <IconButton
                                onClick={handleNewChat}
                                sx={{
                                    width: '100%',
                                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    boxShadow: '0 2px 8px rgba(139,92,246,0.3)',
                                    '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)' },
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        ) : (
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleNewChat}
                                sx={{
                                    fontFamily,
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                    boxShadow: '0 2px 8px rgba(139,92,246,0.3)',
                                    '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)', transform: 'translateY(-1px)' },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                New Task
                            </Button>
                        )}
                    </Box>

                    {!isCollapsed && (
                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search tasks..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 16, color: colors.textTertiary }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        fontFamily, fontSize: '0.75rem', borderRadius: '10px',
                                        bgcolor: 'rgba(0,0,0,0.02)',
                                        '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                                        '&:hover fieldset': { borderColor: 'rgba(139,92,246,0.3) !important' },
                                        '&.Mui-focused fieldset': { borderColor: `${colors.accent} !important` },
                                    },
                                }}
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Content Area */}
            <Box sx={{
                flex: 1, overflow: 'auto', px: isCollapsed ? 0.5 : 1,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' },
            }}>
                {activeView === 'dashboard' ? (
                    /* ===== DASHBOARD: Agent list sorted by recent use ===== */
                    loadingAgentTypes ? (
                        /* Skeleton loader */
                        isCollapsed ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pt: 1 }}>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <Skeleton key={i} variant="rounded" width={38} height={38} sx={{ borderRadius: '10px' }} />
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ py: 1, px: 0.5 }}>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, mb: 0.5 }}>
                                        <Skeleton variant="rounded" width={22} height={22} sx={{ borderRadius: '6px', flexShrink: 0 }} />
                                        <Skeleton variant="text" width={`${60 + Math.random() * 30}%`} height={20} />
                                    </Box>
                                ))}
                            </Box>
                        )
                    ) : isCollapsed ? (
                        /* Collapsed dashboard — icon per agent type with tooltip */
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pt: 1 }}>
                            {sortedAgentTypes.map((section) => {
                                const taskCount = agentStats[section.id]?.count || 0;
                                const sectionColor = section.color || colors.accent;
                                const isActiveFilter = agentTypeFilter === section.id;
                                return (
                                    <Tooltip key={section.id} title={`${section.label}${taskCount > 0 ? ` (${taskCount} task${taskCount !== 1 ? 's' : ''})` : ''}`} placement="right" arrow>
                                        <Box
                                            onClick={() => onResourceClick && onResourceClick(section.id)}
                                            sx={{
                                                width: 38, height: 38, borderRadius: '10px',
                                                bgcolor: isActiveFilter ? `${sectionColor}20` : `${sectionColor}12`,
                                                color: sectionColor,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer',
                                                position: 'relative',
                                                transition: 'all 0.15s ease',
                                                border: isActiveFilter ? `2px solid ${sectionColor}40` : '2px solid transparent',
                                                '&:hover': { bgcolor: `${sectionColor}20`, transform: 'scale(1.05)' },
                                            }}>
                                            {getIcon(section.icon_name, 18)}
                                            {taskCount > 0 && (
                                                <Box sx={{
                                                    position: 'absolute', top: -2, right: -2,
                                                    width: 14, height: 14, borderRadius: '50%',
                                                    bgcolor: sectionColor, color: '#fff',
                                                    fontSize: '0.5rem', fontWeight: 700, fontFamily,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    lineHeight: 1,
                                                }}>
                                                    {taskCount > 9 ? '9+' : taskCount}
                                                </Box>
                                            )}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    ) : (
                        /* Expanded dashboard — flat agent list with task counts */
                        <Box sx={{ py: 0.5 }}>
                            {sortedAgentTypes.map((section) => {
                                const taskCount = agentStats[section.id]?.count || 0;
                                const sectionColor = section.color || colors.accent;
                                const isActiveFilter = agentTypeFilter === section.id;

                                return (
                                    <Box
                                        key={section.id}
                                        onClick={() => onResourceClick && onResourceClick(section.id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1,
                                            px: 1.5, py: 1, mb: 0.5, borderRadius: '8px', cursor: 'pointer',
                                            bgcolor: isActiveFilter ? `${sectionColor}10` : 'transparent',
                                            border: isActiveFilter ? `1.5px solid ${sectionColor}30` : '1.5px solid transparent',
                                            '&:hover': { bgcolor: `${sectionColor}08` },
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <Box sx={{
                                            width: 22, height: 22, borderRadius: '6px',
                                            bgcolor: `${sectionColor}12`, color: sectionColor,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {getIcon(section.icon_name, 14)}
                                        </Box>
                                        <Typography sx={{
                                            fontFamily, fontSize: '0.75rem', fontWeight: 600,
                                            color: isActiveFilter ? sectionColor : colors.textPrimary, flex: 1,
                                        }}>
                                            {section.label}
                                        </Typography>
                                        {taskCount > 0 && (
                                            <Box sx={{
                                                fontSize: '0.6rem', fontWeight: 700, fontFamily,
                                                bgcolor: `${sectionColor}12`, color: sectionColor,
                                                px: 0.75, py: 0.1, borderRadius: '6px', lineHeight: 1.6,
                                                minWidth: 18, textAlign: 'center',
                                            }}>
                                                {taskCount}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )
                ) : (
                    /* ===== CHAT: Conversation List ===== */
                    <>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress size={24} sx={{ color: colors.accent }} />
                            </Box>
                        ) : filtered.length === 0 ? (
                            !isCollapsed && (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.1)', mb: 1 }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
                                        {searchQuery ? 'No tasks found' : 'No tasks yet'}
                                    </Typography>
                                </Box>
                            )
                        ) : (
                            filtered.map((conv) => {
                                const isActive = conv.session_code === currentSessionCode;
                                // Find agent type color from loaded types, fallback to accent
                                const agentDef = agentTypes.find(a => a.id === conv.agent_type);
                                const agentColor = agentDef?.color || colors.accent;
                                const convIcon = (
                                    <Box sx={{
                                        width: 20, height: 20, borderRadius: '6px',
                                        bgcolor: `${agentColor}15`, color: agentColor,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0, mt: 0.25,
                                    }}>
                                        {agentDef ? getIcon(agentDef.icon_name, 12) : <AutoAwesomeIcon sx={{ fontSize: 12 }} />}
                                    </Box>
                                );
                                return (
                                    <Box
                                        key={conv.session_code}
                                        onClick={() => editingId !== conv.session_code && handleSelect(conv.session_code)}
                                        sx={{
                                            display: 'flex', alignItems: 'flex-start', gap: 1,
                                            p: isCollapsed ? 1 : 1.5, mb: 0.5, borderRadius: '10px', cursor: 'pointer',
                                            justifyContent: isCollapsed ? 'center' : 'flex-start',
                                            bgcolor: isActive ? 'rgba(139,92,246,0.08)' : 'transparent',
                                            border: isActive ? '1px solid rgba(139,92,246,0.15)' : '1px solid transparent',
                                            '&:hover': { bgcolor: isActive ? 'rgba(139,92,246,0.12)' : 'rgba(0,0,0,0.03)' },
                                            '&:hover .conv-actions': { opacity: 1 },
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        {isCollapsed ? (
                                            <Tooltip title={conv.title || 'New Task'} placement="right" arrow>
                                                {convIcon}
                                            </Tooltip>
                                        ) : convIcon}
                                        {!isCollapsed && (
                                            <>
                                                {editingId === conv.session_code ? (
                                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <TextField
                                                            size="small" value={editTitle}
                                                            onChange={(e) => setEditTitle(e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleRename(conv.session_code)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            autoFocus
                                                            inputProps={{ maxLength: 60, style: { fontFamily, fontSize: '0.75rem', padding: '4px 8px' } }}
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRename(conv.session_code); }}>
                                                            <CheckIcon sx={{ fontSize: 14, color: '#059669' }} />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingId(null); }}>
                                                            <CloseIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                            <Typography noWrap sx={{
                                                                fontFamily, display: 'block', fontSize: '0.75rem',
                                                                fontWeight: isActive ? 600 : 500,
                                                                color: isActive ? colors.textPrimary : colors.textSecondary,
                                                            }}>
                                                                {conv.title || 'New Task'}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AccessTimeIcon sx={{ fontSize: 11, color: colors.textTertiary }} />
                                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary }}>
                                                                    {formatTime(conv.updated_at)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box className="conv-actions" sx={{ display: 'flex', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}>
                                                            <IconButton size="small" onClick={(e) => startEditing(conv, e)} sx={{ p: 0.25 }}>
                                                                <EditIcon sx={{ fontSize: 12, color: colors.textTertiary }} />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={(e) => handleDelete(conv.session_code, e)} sx={{ p: 0.25 }}>
                                                                <DeleteOutlineIcon sx={{ fontSize: 12, color: '#dc2626' }} />
                                                            </IconButton>
                                                        </Box>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                );
                            })
                        )}
                    </>
                )}
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer anchor="left" open={isOpen} onClose={onToggle} PaperProps={{ sx: { width: 260, borderRadius: '0 14px 14px 0' } }}>
                {sidebarContent}
            </Drawer>
        );
    }

    return (
        <Box sx={{
            width: isCollapsed ? 56 : 240, flexShrink: 0, height: '100%',
            transition: 'width 0.3s ease',
            bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)',
            borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden',
        }}>
            {sidebarContent}
        </Box>
    );
}
